import React, { useState, useEffect, useMemo } from "react";
import { 
  Box, 
  Grid, 
  CircularProgress, 
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
} from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import dayjs from "dayjs";
import {
  XAxis, YAxis, Tooltip, ResponsiveContainer,
  AreaChart, Area, CartesianGrid,
} from "recharts";
import {
  useGetDashboardReportQuery,
  useGetLast7DaysTrafficQuery,
  useGetLastMonthTrafficQuery,
} from "../../store/services/reportApi";
import {useUploaddataqutaQuery} from "../../store/services/createDbApi";
import {useRcsdashboardcountQuery} from "../../store/services/rcstemplateApi";  
import { useConstructionOutlinedQuery } from "../../store/services/whatsappApi";
import StorageIcon from '@mui/icons-material/Storage';
import { jwtDecode } from "jwt-decode";

/* ── Font ── */
const FontImport = () => (
  <style>{`@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');`}</style>
);

/* ── Animated counter ── */
function useCountUp(target, duration = 1200) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!target && target !== 0) return;
    const start = performance.now();
    const num = Number(target);
    const frame = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.floor(eased * num));
      if (progress < 1) requestAnimationFrame(frame);
      else setValue(num);
    };
    requestAnimationFrame(frame);
  }, [target, duration]);
  return value;
}

/* ── SVG Icons (all kept as original) ── */
const SmsIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);
const RcsIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z" />
    <path d="m9 12 2 2 4-4" />
  </svg>
);
const WaIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
  </svg>
);
const SentIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13" />
    <polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
);
const DeliveredIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);
const FailedIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="15" y1="9" x2="9" y2="15" />
    <line x1="9" y1="9" x2="15" y2="15" />
  </svg>
);
const AwaitedIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);
const DownloadIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

/* ── Service Selector Button ── */
function ServiceBtn({ label, active, color, lightColor, icon, onClick }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        display: "flex", alignItems: "center", gap: 1,
        px: 2, py: 1, borderRadius: "12px", cursor: "pointer",
        border: active ? `2px solid ${color}` : "1.5px solid #e2e8f0",
        background: active ? lightColor : "rgba(255,255,255,0.7)",
        backdropFilter: "blur(6px)",
        WebkitBackdropFilter: "blur(6px)",
        transition: "all 0.2s ease",
        "&:hover": { background: lightColor, borderColor: color },
      }}
    >
      <Box sx={{ color: active ? color : "#94a3b8", display: "flex" }}>{icon}</Box>
      <Typography sx={{
        fontSize: "12px", fontWeight: active ? 700 : 500,
        color: active ? color : "#64748b",
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        userSelect: "none",
      }}>
        {label}
      </Typography>
    </Box>
  );
}

/* ── Animated Bar Row ── */
function BarRow({ label, value, icon, barColor, max, delay }) {
  const animated = useCountUp(value, 1000 + delay * 120);
  const [barWidth, setBarWidth] = useState(0);
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;

  useEffect(() => {
    setBarWidth(0);
    const t = setTimeout(() => setBarWidth(pct), 180 + delay * 130);
    return () => clearTimeout(t);
  }, [pct, delay, value]);

  return (
    <Box sx={{
      display: "flex", alignItems: "center", gap: 2,
      opacity: 0,
      animation: "rowIn 0.4s ease forwards",
      animationDelay: `${0.08 + delay * 0.1}s`,
      "@keyframes rowIn": {
        from: { opacity: 0, transform: "translateX(-10px)" },
        to: { opacity: 1, transform: "translateX(0)" },
      },
    }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, minWidth: 120, flexShrink: 0 }}>
        <Box sx={{
          width: 36, height: 36, borderRadius: "10px", flexShrink: 0,
          background: `${barColor}15`, border: `1px solid ${barColor}30`,
          display: "flex", alignItems: "center", justifyContent: "center",
          color: barColor,
        }}>
          {icon}
        </Box>
        <Box>
          <Typography sx={{ fontSize: "12px", fontWeight: 700, color: "#1a1a2e", fontFamily: "'Plus Jakarta Sans', sans-serif", lineHeight: 1.2 }}>
            {label}
          </Typography>
          <Typography sx={{ fontSize: "10px", color: "#94a3b8", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            {pct}% of total
          </Typography>
        </Box>
      </Box>

      <Box sx={{ flex: 1 }}>
        <Box sx={{ position: "relative", width: "100%", height: 36, borderRadius: "10px", background: "#f1f5f9", overflow: "hidden" }}>
          <Box sx={{
            position: "absolute", left: 0, top: 0, bottom: 0,
            width: `${barWidth}%`, borderRadius: "10px",
            background: `linear-gradient(90deg, ${barColor}ee, ${barColor}99)`,
            transition: "width 0.95s cubic-bezier(0.34,1.05,0.64,1)",
            display: "flex", alignItems: "center", justifyContent: "flex-end", pr: 1.2,
          }}>
            {pct > 20 && (
              <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#fff", fontFamily: "'Plus Jakarta Sans', sans-serif", whiteSpace: "nowrap" }}>
                {animated.toLocaleString()}
              </Typography>
            )}
          </Box>
          {pct <= 20 && (
            <Typography sx={{
              position: "absolute",
              left: `calc(${barWidth}% + 8px)`, top: "50%", transform: "translateY(-50%)",
              fontSize: "11px", fontWeight: 700, color: "#475569",
              fontFamily: "'Plus Jakarta Sans', sans-serif", whiteSpace: "nowrap",
            }}>
              {animated.toLocaleString()}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
}

/* ── Custom Tooltip ── */
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <Box sx={{ background: "#1a1a2e", borderRadius: "10px", px: 1.5, py: 1, border: "1px solid rgba(255,255,255,0.08)" }}>
      <Typography sx={{ color: "rgba(255,255,255,0.45)", fontSize: "10px", mb: 0.3, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
        {label}
      </Typography>
      <Typography sx={{ color: "#a78bfa", fontSize: "13px", fontWeight: 700, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
        {Number(payload[0].value).toLocaleString()}
      </Typography>
    </Box>
  );
};

/* ── Donut Ring ── */
function DonutRing({ percentage }) {
  const r    = 46;
  const circ = 2 * Math.PI * r;
  const [dash, setDash] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => setDash((percentage / 100) * circ), 400);
    return () => clearTimeout(t);
  }, [percentage, circ]);

  return (
    <Box sx={{ position: "relative", display: "inline-flex" }}>
      <svg width="112" height="112" style={{ transform: "rotate(-90deg)" }}>
        <defs>
          <linearGradient id="donutG" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%"   stopColor="#7C3AED" />
            <stop offset="100%" stopColor="#06B6D4" />
          </linearGradient>
        </defs>
        <circle cx="56" cy="56" r={r} fill="none" stroke="#f1f5f9" strokeWidth="8" />
        <circle cx="56" cy="56" r={r} fill="none" stroke="url(#donutG)" strokeWidth="8"
          strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={circ - dash}
          style={{ transition: "stroke-dashoffset 1.3s cubic-bezier(0.34,1.3,0.64,1)" }} />
      </svg>
      <Box sx={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
        <Typography sx={{ fontSize: "19px", fontWeight: 800, color: "#1a1a2e", lineHeight: 1, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          {percentage}%
        </Typography>
        <Typography sx={{ fontSize: "8px", color: "#94a3b8", fontWeight: 700, letterSpacing: "0.06em", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          TARGET
        </Typography>
      </Box>
    </Box>
  );
}

/* ── Simple Stat Card (for SMS/RCS) ── */
function SimpleStatCard({ label, value, accent, icon, delay }) {
  const animated = useCountUp(value, 1000 + delay * 100);
  return (
    <Box sx={{
      background: "rgba(255,255,255,0.7)",
      backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)",
      border: "1.5px solid rgba(240,240,247,0.5)", borderRadius: "16px",
      p: "14px 18px", display: "flex", alignItems: "center", gap: 1.5,
      opacity: 0,
      animation: "cardIn 0.45s cubic-bezier(0.34,1.3,0.64,1) forwards",
      animationDelay: `${0.05 + delay * 0.08}s`,
      transition: "transform 0.2s, box-shadow 0.2s",
      "&:hover": { transform: "translateY(-2px)", boxShadow: "0 8px 24px rgba(0,0,0,0.07)" },
      "@keyframes cardIn": {
        from: { opacity: 0, transform: "translateY(14px)" },
        to: { opacity: 1, transform: "translateY(0)" },
      },
    }}>
      <Box sx={{
        width: 44, height: 44, borderRadius: "12px", flexShrink: 0,
        background: `${accent}15`, border: `1px solid ${accent}25`,
        display: "flex", alignItems: "center", justifyContent: "center", fontSize: "20px",
      }}>
        {icon}
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography sx={{ fontSize: "10px", fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.07em", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: "22px", fontWeight: 800, color: "#1a1a2e", lineHeight: 1.1, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          {animated.toLocaleString()}
        </Typography>
      </Box>
      <Box sx={{ width: 3, height: 30, borderRadius: 99, background: accent, opacity: 0.7, flexShrink: 0 }} />
    </Box>
  );
}

/* ── Detailed Stat Card (for WhatsApp) ── */
function DetailedStatCard({ label, period, accent, icon, delay }) {
  return (
    <Box sx={{
      background: "rgba(255,255,255,0.7)",
      backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)",
      border: "1.5px solid rgba(240,240,247,0.5)", borderRadius: "16px",
      p: "14px 18px",
      opacity: 0,
      animation: "cardIn 0.45s cubic-bezier(0.34,1.3,0.64,1) forwards",
      animationDelay: `${0.05 + delay * 0.08}s`,
      transition: "transform 0.2s, box-shadow 0.2s",
      "&:hover": { transform: "translateY(-2px)", boxShadow: "0 8px 24px rgba(0,0,0,0.07)" },
      "@keyframes cardIn": {
        from: { opacity: 0, transform: "translateY(14px)" },
        to: { opacity: 1, transform: "translateY(0)" },
      },
    }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
        <Box sx={{
          width: 36, height: 36, borderRadius: "10px", flexShrink: 0,
          background: `${accent}15`, border: `1px solid ${accent}25`,
          display: "flex", alignItems: "center", justifyContent: "center", fontSize: "18px",
        }}>
          {icon}
        </Box>
        <Typography sx={{
          fontSize: "11px", fontWeight: 600, color: "#64748b",
          textTransform: "uppercase", letterSpacing: "0.06em",
          fontFamily: "'Plus Jakarta Sans', sans-serif",
        }}>
          {label}
        </Typography>
      </Box>

      {period.isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
          <CircularProgress size={18} sx={{ color: accent }} />
        </Box>
      ) : period.isError ? (
        <Typography sx={{ fontSize: "12px", color: "#DC2626", fontFamily: "'Plus Jakarta Sans', sans-serif", textAlign: "center" }}>
          Error loading
        </Typography>
      ) : (
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          {[
            { key: "Sent",      value: period.sent,      color: "#7C3AED" },
            { key: "Delivered", value: period.delivered,  color: "#059669" },
            { key: "Awaited",   value: period.awaited,    color: "#D97706" },
            { key: "Failed",    value: period.failed,     color: "#DC2626" },
          ].map(({ key, value, color }) => (
            <Box key={key} sx={{
              flex: "1 1 auto",
              background: `${color}10`,
              border: `1px solid ${color}30`,
              borderRadius: "8px",
              textAlign: "center",
              py: 0.6,
              px: 0.5,
            }}>
              <Typography sx={{ fontSize: "9px", color: "#94a3b8", fontFamily: "'Plus Jakarta Sans', sans-serif", lineHeight: 1 }}>
                {key}
              </Typography>
              <Typography sx={{ fontSize: "14px", fontWeight: 700, color, fontFamily: "'Plus Jakarta Sans', sans-serif", lineHeight: 1.2 }}>
                {value.toLocaleString()}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

/* ── Download usage colour scale ── */
function getDownloadColor(pct) {
  if (pct <= 30) return "#8b8d8b";
  if (pct <= 50) return "#8b8d8b";
  if (pct <= 70) return "#8b8d8b";
  return "#DC2626";
}
function getDownloadZoneLabel(pct) {
  if (pct <= 30) return "Low usage";
  if (pct <= 50) return "Moderate usage";
  if (pct <= 70) return "High usage";
  return "Critical usage";
}

/* ── Download Progress Card with Add Space Button ── */
function DownloadProgressCard({ 
  percentage = 0, 
  downloaded, 
  total, 
  isLoading, 
  isError, 
  delay = 4,
  onAddMoreSpace 
}) {
  const clamped = Math.min(100, Math.max(0, Number(percentage) || 0));
  const [barWidth, setBarWidth] = useState(0);
  const color = getDownloadColor(clamped);
  const zoneLabel = getDownloadZoneLabel(clamped);
  const animatedPct = useCountUp(clamped, 1200);
  
  // State for popup
  const [openAddSpace, setOpenAddSpace] = useState(false);
  const [additionalSpace, setAdditionalSpace] = useState(10);
  const [spaceUnit, setSpaceUnit] = useState('MB');

  useEffect(() => {
    if (isLoading) return;
    setBarWidth(0);
    const t = setTimeout(() => setBarWidth(clamped), 250 + delay * 100);
    return () => clearTimeout(t);
  }, [clamped, delay, isLoading]);

  const handleOpenAddSpace = () => {
    setOpenAddSpace(true);
  };

  const handleCloseAddSpace = () => {
    setOpenAddSpace(false);
  };

  const handleAddSpaceSubmit = () => {
    if (onAddMoreSpace) {
      onAddMoreSpace({
        additionalSpace,
        unit: spaceUnit,
      });
    }
    handleCloseAddSpace();
  };

  return (
    <>
      <Box sx={{
        background: "rgba(255,255,255,0.7)",
        backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)",
        border: "1.5px solid rgba(240,240,247,0.5)", borderRadius: "16px",
        p: "16px 20px",
        opacity: 0,
        animation: "cardIn 0.45s cubic-bezier(0.34,1.3,0.64,1) forwards",
        animationDelay: `${0.05 + delay * 0.08}s`,
        transition: "transform 0.2s, box-shadow 0.2s",
        "&:hover": { transform: "translateY(-2px)", boxShadow: "0 8px 24px rgba(0,0,0,0.07)" },
        "@keyframes cardIn": {
          from: { opacity: 0, transform: "translateY(14px)" },
          to: { opacity: 1, transform: "translateY(0)" },
        },
        "@keyframes stripeMove": {
          from: { backgroundPosition: "0 0" },
          to: { backgroundPosition: "28px 0" },
        },
      }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1, mb: 1.8 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
            <Box sx={{
              width: 36, height: 36, borderRadius: "10px", flexShrink: 0,
              background: `${color}15`, border: `1px solid ${color}30`,
              display: "flex", alignItems: "center", justifyContent: "center",
              color, transition: "color 0.5s ease, background 0.5s ease, border 0.5s ease",
            }}>
              <StorageIcon />
            </Box>
            <Box>
              <Typography sx={{ fontSize: "12px", fontWeight: 700, color: "#1a1a2e", fontFamily: "'Plus Jakarta Sans', sans-serif", lineHeight: 1.2 }}>
                DB Space Usage
              </Typography>
              <Typography sx={{ fontSize: "10px", color: "#94a3b8", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                {isError
                  ? "Failed to load usage"
                  : isLoading
                  ? "Loading usage…"
                  : downloaded != null && total != null
                  ? `${Number(downloaded).toFixed(2)} MB of ${Number(total).toLocaleString()} MB used`
                  : zoneLabel}
              </Typography>
            </Box>
          </Box>
          
          {/* Right side with percentage and add button */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box sx={{
              px: 1.3, py: 0.4, borderRadius: "8px",
              background: `${color}15`, border: `1px solid ${color}40`,
              transition: "background 0.5s ease, border 0.5s ease",
            }}>
              <Typography sx={{ fontSize: "14px", fontWeight: 800, color, fontFamily: "'Plus Jakarta Sans', sans-serif", transition: "color 0.5s ease" }}>
                {isLoading ? "…" : `${animatedPct}%`}
              </Typography>
            </Box>
            
            {/* Add More Space Button */}
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenAddSpace}
              sx={{
                background: "linear-gradient(135deg, #7C3AED 0%, #6D28D9 100%)",
                color: "#fff",
                borderRadius: "8px",
                px: 1.5,
                py: 0.6,
                fontSize: "11px",
                fontWeight: 700,
                textTransform: "none",
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                boxShadow: "0 2px 8px rgba(124, 58, 237, 0.3)",
                "&:hover": {
                  background: "linear-gradient(135deg, #6D28D9 0%, #5B21B6 100%)",
                  boxShadow: "0 4px 12px rgba(124, 58, 237, 0.4)",
                },
              }}
            >
              Add Space
            </Button>
          </Box>
        </Box>

        {/* ── Loading line ── */}
        <Box sx={{ position: "relative", width: "100%", height: 16, borderRadius: 99, background: "#f1f5f9", overflow: "hidden" }}>
          {/* zone boundary ticks at 30 / 50 / 70 */}
          {[30, 50, 70].map((mark) => (
            <Box key={mark} sx={{
              position: "absolute", left: `${mark}%`, top: 0, bottom: 0,
              width: "2px", background: "rgba(255,255,255,0.85)", zIndex: 2,
            }} />
          ))}
          <Box sx={{
            position: "absolute", left: 0, top: 0, bottom: 0,
            width: `${barWidth}%`, borderRadius: 99, overflow: "hidden",
            background: `linear-gradient(90deg, ${color}cc, ${color})`,
            transition: "width 1.1s cubic-bezier(0.34,1.05,0.64,1), background 0.6s ease",
            boxShadow: `0 0 10px ${color}55`,
          }}>
            <Box sx={{
              position: "absolute", inset: 0,
              backgroundImage: "repeating-linear-gradient(45deg, rgba(255,255,255,0.25) 0, rgba(255,255,255,0.25) 8px, transparent 8px, transparent 16px)",
              backgroundSize: "28px 28px",
              animation: clamped > 0 && clamped < 100 ? "stripeMove 1s linear infinite" : "none",
            }} />
          </Box>
        </Box>

        {/* ── 0–100 scale ruler ── */}
        <Box sx={{ position: "relative", width: "100%", height: 16, mt: 0.5 }}>
          {[0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100].map((mark) => (
            <Box
              key={mark}
              sx={{
                position: "absolute",
                left: `${mark}%`,
                top: 0,
                transform:
                  mark === 0 ? "translateX(0)"
                  : mark === 100 ? "translateX(-100%)"
                  : "translateX(-50%)",
                display: "flex", flexDirection: "column", alignItems: "center",
              }}
            >
              <Box sx={{ width: "1px", height: 4, background: "#cbd5e1" }} />
              <Typography sx={{
                fontSize: "8px", color: "#94a3b8",
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                lineHeight: 1.4, mt: 0.2,
              }}>
                {mark}
              </Typography>
            </Box>
          ))}
        </Box>

        <Typography sx={{ fontSize: "10px", color: "#94a3b8", fontFamily: "'Plus Jakarta Sans', sans-serif", mt: 1 }}>
          {clamped.toFixed(2)}% downloaded • {(100 - clamped).toFixed(2)}% remaining
        </Typography>
      </Box>

      {/* Add More Space Dialog */}
      <Dialog
        open={openAddSpace}
        onClose={handleCloseAddSpace}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            background: "rgba(255,255,255,0.95)",
            backdropFilter: "blur(12px)",
            boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
          },
        }}
      >
        <DialogTitle sx={{ 
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          fontWeight: 700,
          fontSize: "18px",
          color: "#1a1a2e",
        }}>
          Add More Storage Space
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <Typography sx={{ 
              fontSize: "12px", 
              color: "#64748b", 
              mb: 2,
              fontFamily: "'Plus Jakarta Sans', sans-serif",
            }}>
              Select additional storage space to add to your account.
            </Typography>
            
            {/* Current Usage Info */}
            <Box sx={{
              background: "#f8fafc",
              border: "1px solid #e2e8f0",
              borderRadius: "10px",
              p: 2,
              mb: 2,
            }}>
              <Typography sx={{ fontSize: "11px", color: "#94a3b8", mb: 1, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                Current Usage
              </Typography>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#1a1a2e", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  {Number(downloaded || 0).toFixed(2)} MB used
                </Typography>
                <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#1a1a2e", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  {Number(total || 0).toLocaleString()} MB total
                </Typography>
              </Box>
            </Box>

            {/* Space Options */}
            <Typography sx={{ fontSize: "12px", fontWeight: 600, color: "#1a1a2e", mb: 1, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              Select Additional Space
            </Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1, mb: 2 }}>
              {[10, 50, 100, 500, 1000, 5000].map((space) => (
                <Box
                  key={space}
                  onClick={() => setAdditionalSpace(space)}
                  sx={{
                    border: additionalSpace === space ? "2px solid #7C3AED" : "1px solid #e2e8f0",
                    borderRadius: "8px",
                    p: 1.5,
                    textAlign: "center",
                    cursor: "pointer",
                    transition: "all 0.2s",
                    background: additionalSpace === space ? "#f5f3ff" : "transparent",
                    "&:hover": {
                      borderColor: "#7C3AED",
                      background: "#f5f3ff",
                    },
                  }}
                >
                  <Typography sx={{ 
                    fontSize: "14px", 
                    fontWeight: 700, 
                    color: additionalSpace === space ? "#7C3AED" : "#1a1a2e",
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                  }}>
                    {space >= 1000 ? `${space/1000} GB` : `${space} MB`}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* Custom Space Input */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <TextField
                type="number"
                size="small"
                value={additionalSpace}
                onChange={(e) => setAdditionalSpace(Number(e.target.value))}
                sx={{
                  flex: 1,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Select
                        value={spaceUnit}
                        onChange={(e) => setSpaceUnit(e.target.value)}
                        size="small"
                        sx={{
                          fontSize: "11px",
                          "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                        }}
                      >
                        <MenuItem value="MB">MB</MenuItem>
                        <MenuItem value="GB">GB</MenuItem>
                      </Select>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseAddSpace} sx={{ 
            color: "#64748b",
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontWeight: 600,
          }}>
            Cancel
          </Button>
          <Button 
            onClick={handleAddSpaceSubmit}
            variant="contained"
            sx={{
              background: "linear-gradient(135deg, #7C3AED 0%, #6D28D9 100%)",
              color: "#fff",
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontWeight: 700,
              px: 2,
              "&:hover": {
                background: "linear-gradient(135deg, #6D28D9 0%, #5B21B6 100%)",
              },
            }}
          >
            Add Space
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

/* ════════════════════════════════════════════════
   MAIN DASHBOARD COMPONENT
   ════════════════════════════════════════════════ */
const Dashboard = () => {
  const [activeService, setActiveService] = useState("SMS");

  // ── Extract customerId from JWT token ──
  const [customerId, setCustomerId] = useState(null);
  useEffect(() => {
    try {
      const token = localStorage.getItem("accessToken");
      if (!token) {
        console.warn("No access token found");
        return;
      }
      const decoded = jwtDecode(token);
      const id =
        decoded?.customer_id || decoded?.id || decoded?.userid || null;
      if (id) {
        setCustomerId(id);
      } else {
        console.warn("Customer ID not found in token");
      }
    } catch (err) {
      console.error("Invalid access token format", err);
    }
  }, []);

  // ── Download usage from useUploaddataqutaQuery ──
  const {
    data: uploadQuotaData,
    isLoading: quotaLoading,
    isError: quotaError,
  } = useUploaddataqutaQuery();

  const quota = uploadQuotaData?.data;
  const downloadPercentage = quota?.usedPercentage ?? 0;
  const downloadedMB = quota?.totalUsedMB ?? 0;
  const totalMB = quota?.uploadLimitMB ?? 0;

  /* ── SMS / General queries ── */
  const { data: dashboardData, isLoading, isError } = useGetDashboardReportQuery();
  const { data: lastMonthData, isLoading: lmLoading, isError: lmError } = useGetLastMonthTrafficQuery();
  const { data: last7DaysData } = useGetLast7DaysTrafficQuery();

  /* ── WhatsApp period queries ── */
  const todayWa = useConstructionOutlinedQuery(
    { customerId, start_date: dayjs().format("YYYY-MM-DD"), end_date: dayjs().format("YYYY-MM-DD") },
    { skip: activeService !== "WHATSAPP" || !customerId }
  );
  const yesterdayWa = useConstructionOutlinedQuery(
    { customerId, start_date: dayjs().subtract(1, "day").format("YYYY-MM-DD"), end_date: dayjs().subtract(1, "day").format("YYYY-MM-DD") },
    { skip: activeService !== "WHATSAPP" || !customerId }
  );
  const last7DaysWa = useConstructionOutlinedQuery(
    { customerId, start_date: dayjs().subtract(6, "day").format("YYYY-MM-DD"), end_date: dayjs().format("YYYY-MM-DD") },
    { skip: activeService !== "WHATSAPP" || !customerId }
  );
  const thisMonthWa = useConstructionOutlinedQuery(
    { customerId, start_date: dayjs().startOf("month").format("YYYY-MM-DD"), end_date: dayjs().format("YYYY-MM-DD") },
    { skip: activeService !== "WHATSAPP" || !customerId }
  );

  /* ── RCS period queries ── */
  const todayRcs = useRcsdashboardcountQuery(
    { startdate: dayjs().format("YYYY-MM-DD"), enddate: dayjs().format("YYYY-MM-DD") },
    { skip: activeService !== "RCS" }
  );
  const yesterdayRcs = useRcsdashboardcountQuery(
    { startdate: dayjs().subtract(1, "day").format("YYYY-MM-DD"), enddate: dayjs().subtract(1, "day").format("YYYY-MM-DD") },
    { skip: activeService !== "RCS" }
  );
  const last7DaysRcs = useRcsdashboardcountQuery(
    { startdate: dayjs().subtract(6, "day").format("YYYY-MM-DD"), enddate: dayjs().format("YYYY-MM-DD") },
    { skip: activeService !== "RCS" }
  );
  const thisMonthRcs = useRcsdashboardcountQuery(
    { startdate: dayjs().startOf("month").format("YYYY-MM-DD"), enddate: dayjs().format("YYYY-MM-DD") },
    { skip: activeService !== "RCS" }
  );

  // ── Compute WhatsApp period totals ──
  const waPeriodData = useMemo(() => {
    const queries = [todayWa, yesterdayWa, last7DaysWa, thisMonthWa];
    return queries.map((query) => {
      const data = query.data?.data;
      if (data?.summary && Array.isArray(data.summary)) {
        const totals = data.summary.reduce(
          (acc, day) => ({
            sent_count: acc.sent_count + (Number(day.sent_count) || 0),
            delivered_count: acc.delivered_count + (Number(day.delivered_count) || 0),
            awaited_count: acc.awaited_count + (Number(day.awaited_count) || 0),
            failed_count: acc.failed_count + (Number(day.failed_count) || 0),
          }),
          { sent_count: 0, delivered_count: 0, awaited_count: 0, failed_count: 0 }
        );
        return {
          sent: totals.sent_count,
          delivered: totals.delivered_count,
          awaited: totals.awaited_count,
          failed: totals.failed_count,
          isLoading: query.isLoading,
          isError: query.isError,
        };
      }
      const totals = data?.totals || {};
      return {
        sent: Number(totals.sent_count || 0),
        delivered: Number(totals.delivered_count || 0),
        awaited: Number(totals.awaited_count || 0),
        failed: Number(totals.failed_count || 0),
        isLoading: query.isLoading,
        isError: query.isError,
      };
    });
  }, [todayWa, yesterdayWa, last7DaysWa, thisMonthWa]);

  // ── Compute RCS period totals ──
  const rcsPeriodData = useMemo(() => {
    const queries = [todayRcs, yesterdayRcs, last7DaysRcs, thisMonthRcs];
    return queries.map((query) => {
      const data = query.data?.data;
      if (data) {
        return {
          sent: Number(data.sent || 0),
          delivered: Number(data.delivered || 0),
          awaited: Number(data.pending || 0),
          failed: Number(data.failed || 0),
          isLoading: query.isLoading,
          isError: query.isError,
        };
      }
      return {
        sent: 0,
        delivered: 0,
        awaited: 0,
        failed: 0,
        isLoading: query.isLoading,
        isError: query.isError,
      };
    });
  }, [todayRcs, yesterdayRcs, last7DaysRcs, thisMonthRcs]);

  const trafficValues = useMemo(
    () => [
      Number(dashboardData?.dashboard?.todaysTraffic ?? 0),
      Number(dashboardData?.dashboard?.yesterdaysTraffic ?? 0),
      Number(dashboardData?.dashboard?.last7DayTraffic ?? 0),
      Number(dashboardData?.dashboard?.monthTillDateTraffic ?? 0),
    ],
    [dashboardData]
  );

  /* ── Today's snapshot for bars ── */
  const todayStats = useMemo(() => {
    if (activeService === "SMS") {
      const s = trafficValues[0];
      return { sent: s, delivered: Math.round(s * 0.87), failed: Math.round(s * 0.05), awaited: 0 };
    }
    if (activeService === "RCS") {
      const r = rcsPeriodData[0] || {};
      return {
        sent: r.sent ?? 0,
        delivered: r.delivered ?? 0,
        failed: r.failed ?? 0,
        awaited: r.awaited ?? 0,
      };
    }
    if (activeService === "WHATSAPP") {
      const t = waPeriodData[0] || {};
      return {
        sent: t.sent ?? 0,
        delivered: t.delivered ?? 0,
        failed: t.failed ?? 0,
        awaited: t.awaited ?? 0,
      };
    }
    return {};
  }, [activeService, trafficValues, waPeriodData, rcsPeriodData]);

  const active = todayStats;
  const maxBar = Math.max(active.sent, active.delivered, active.failed, active.awaited ?? 0, 1);

  const barRows = (activeService === "WHATSAPP" || activeService === "RCS")
    ? [
        { label: "Sent",      value: active.sent,      icon: <SentIcon />,      barColor: "#7C3AED" },
        { label: "Delivered", value: active.delivered,  icon: <DeliveredIcon />, barColor: "#059669" },
        { label: "Awaited",   value: active.awaited,    icon: <AwaitedIcon />,   barColor: "#D97706" },
        { label: "Failed",    value: active.failed,     icon: <FailedIcon />,    barColor: "#DC2626" },
      ]
    : [
        { label: "Sent",      value: active.sent,      icon: <SentIcon />,      barColor: "#7C3AED" },
        { label: "Delivered", value: active.delivered,  icon: <DeliveredIcon />, barColor: "#059669" },
        { label: "Failed",    value: active.failed,     icon: <FailedIcon />,    barColor: "#DC2626" },
      ];

  const deliveryRate = active.sent > 0 ? Math.round((active.delivered / active.sent) * 100) : 0;

  // ── Chart data ──
  const smsOrRcsChartData =
    last7DaysData?.last7DaysTraffic?.map((d) => ({ date: d.date, traffic: d.traffic })) || [];

  const whatsappChartData = useMemo(() => {
    if (activeService !== "WHATSAPP") return [];
    const summary = last7DaysWa.data?.data?.summary;
    if (!summary || !Array.isArray(summary)) return [];
    return summary.map((day) => ({
      date: day.date,
      traffic: Number(day.sent_count) || 0,
    }));
  }, [activeService, last7DaysWa.data]);

  const rcsChartData = useMemo(() => {
    if (activeService !== "RCS") return [];
    const data = last7DaysRcs.data?.data;
    if (!data) return [];
    return [{
      date: dayjs().format("YYYY-MM-DD"),
      traffic: Number(data.sent || 0),
    }];
  }, [activeService, last7DaysRcs.data]);

  const chartData = activeService === "WHATSAPP" ? whatsappChartData : activeService === "RCS" ? rcsChartData : smsOrRcsChartData;
  const chartColor = activeService === "WHATSAPP" ? "#25D366" : activeService === "RCS" ? "#059669" : "#7C3AED";

  // Handle add more space
  const handleAddMoreSpace = (spaceData) => {
    console.log("Add more space:", spaceData);
    // Here you can call your API to add more space
    // Example: addStorageSpaceMutation({ additionalSpace: spaceData.additionalSpace, unit: spaceData.unit })
  };

  // ── Loading / Error states ──
  if (isLoading || lmLoading) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "60vh", gap: 2 }}>
        <CircularProgress size={28} sx={{ color: "#7C3AED" }} />
        <Typography sx={{ color: "#94a3b8", fontSize: "13px", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          Loading dashboard…
        </Typography>
      </Box>
    );
  }
  if (isError || lmError) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "60vh" }}>
        <Typography sx={{ color: "#fda4af", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          Failed to load Dashboard
        </Typography>
      </Box>
    );
  }

  const lmt = lastMonthData?.lastMonthTraffic || {};
  const percentage = lmt.spendingsTarget ? parseInt(lmt.spendingsTarget, 10) : Number(lmt.percentage || 0);

  const CARD_LABELS = ["Today's Traffic", "Yesterday", "Last 7 Days", "Month Till Date"];
  const CARD_ACCENTS = ["#7C3AED", "#3B82F6", "#059669", "#D97706"];
  const CARD_ICONS = ["📈", "📊", "📉", "🗓️"];

  return (
    <>
      <FontImport />
      <Box sx={{ minHeight: "100vh", background: "transparent", p: { xs: 1.5, md: 2.5 }, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
        {/* Header */}
        <Box sx={{ mb: 2.5, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
          <Box>
            <Typography sx={{ fontSize: "22px", fontWeight: 800, color: "#1a1a2e", fontFamily: "'Plus Jakarta Sans', sans-serif", letterSpacing: "-0.4px" }}>
              Dashboard
            </Typography>
            <Typography sx={{ fontSize: "12px", color: "#94a3b8", fontFamily: "'Plus Jakarta Sans', sans-serif", mt: 0.2 }}>
              Here's what's happening with your traffic today.
            </Typography>
          </Box>
          <Box sx={{
            display: "flex", alignItems: "center", gap: 0.8,
            background: "rgba(255,255,255,0.7)", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)",
            border: "1.5px solid rgba(226,232,240,0.5)", borderRadius: "10px", px: 1.5, py: 0.7,
          }}>
            <Box sx={{
              width: 7, height: 7, borderRadius: "50%", background: "#22c55e",
              boxShadow: "0 0 6px #22c55e",
              animation: "blink 1.4s ease-in-out infinite",
              "@keyframes blink": { "0%,100%": { opacity: 1 }, "50%": { opacity: 0.3 } },
            }} />
            <Typography sx={{ color: "#475569", fontSize: "11px", fontWeight: 700, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              Live
            </Typography>
          </Box>
        </Box>

        {/* ── Stat Cards ── */}
        <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
          {activeService === "WHATSAPP"
            ? waPeriodData.map((period, i) => (
                <Grid item xs={12} sm={6} md={3} key={i}>
                  <DetailedStatCard
                    label={CARD_LABELS[i]}
                    period={period}
                    accent={CARD_ACCENTS[i]}
                    icon={CARD_ICONS[i]}
                    delay={i}
                  />
                </Grid>
              ))
            : activeService === "RCS"
            ? rcsPeriodData.map((period, i) => (
                <Grid item xs={12} sm={6} md={3} key={i}>
                  <DetailedStatCard
                    label={CARD_LABELS[i]}
                    period={period}
                    accent={CARD_ACCENTS[i]}
                    icon={CARD_ICONS[i]}
                    delay={i}
                  />
                </Grid>
              ))
            : trafficValues.map((val, i) => (
                <Grid item xs={12} sm={6} md={3} key={i}>
                  <SimpleStatCard
                    label={CARD_LABELS[i]}
                    value={val}
                    accent={CARD_ACCENTS[i]}
                    icon={CARD_ICONS[i]}
                    delay={i}
                  />
                </Grid>
              ))}
        </Grid>

        {/* ── Download Usage (5th card, full width, below the 4 stat cards) ── */}
        <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
          <Grid item xs={12}>
            <DownloadProgressCard
              percentage={downloadPercentage}
              downloaded={downloadedMB}
              total={totalMB}
              isLoading={quotaLoading}
              isError={quotaError}
              delay={4}
              onAddMoreSpace={handleAddMoreSpace}
            />
          </Grid>
        </Grid>

        {/* ── Message Delivery Overview ── */}
        <Box sx={{
          background: "rgba(255,255,255,0.7)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)",
          border: "1.5px solid rgba(240,240,247,0.5)", borderRadius: "20px",
          p: { xs: 2, md: 3 }, mb: 2.5,
          opacity: 0,
          animation: "cardIn 0.45s cubic-bezier(0.34,1.3,0.64,1) 0.32s forwards",
          "@keyframes cardIn": {
            from: { opacity: 0, transform: "translateY(14px)" },
            to: { opacity: 1, transform: "translateY(0)" },
          },
        }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 2, mb: 2.5 }}>
            <Box>
              <Typography sx={{ fontSize: "15px", fontWeight: 700, color: "#1a1a2e", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                Message Delivery Overview
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.4, flexWrap: "wrap" }}>
                <Typography sx={{ fontSize: "11px", color: "#94a3b8", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  {activeService === "WHATSAPP"
                    ? "Today's WhatsApp message performance"
                    : activeService === "RCS"
                    ? "Today's RCS message performance"
                    : "Today's SMS message performance"
                  }
                </Typography>
                <Box sx={{
                  px: 1, py: 0.25, borderRadius: "6px",
                  background: active.sent > 0 ? "#dcfce7" : "#f1f5f9",
                  border: `1px solid ${active.sent > 0 ? "#86efac" : "#e2e8f0"}`,
                }}>
                  <Typography sx={{
                    fontSize: "9px", fontWeight: 700, letterSpacing: "0.05em",
                    color: active.sent > 0 ? "#16a34a" : "#64748b",
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                  }}>
                    {active.sent > 0 ? "Active" : "No activity"}
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              {[
                { key: "SMS", label: "SMS", icon: <SmsIcon />, color: "#7C3AED", lightColor: "#EDE9FE" },
                { key: "RCS", label: "RCS", icon: <RcsIcon />, color: "#059669", lightColor: "#D1FAE5" },
                { key: "WHATSAPP", label: "WhatsApp", icon: <WaIcon />, color: "#16A34A", lightColor: "#DCFCE7" },
              ].map((s) => (
                <ServiceBtn
                  key={s.key}
                  label={s.label}
                  active={activeService === s.key}
                  color={s.color}
                  lightColor={s.lightColor}
                  icon={s.icon}
                  onClick={() => setActiveService(s.key)}
                />
              ))}
            </Box>
          </Box>

          {/* Bar rows */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, mb: 3 }}>
            {barRows.map((row, i) => (
              <BarRow key={`${activeService}-${row.label}`} {...row} max={maxBar} delay={i} />
            ))}
          </Box>

          {/* Footer */}
          <Box sx={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            pt: 2, borderTop: "1px solid #f1f5f9", flexWrap: "wrap", gap: 1,
          }}>
            <Box sx={{ display: "flex", gap: 2.5, flexWrap: "wrap" }}>
              {(activeService === "WHATSAPP" || activeService === "RCS"
                ? [
                    { label: "Sent", color: "#7C3AED" },
                    { label: "Delivered", color: "#059669" },
                    { label: "Awaited", color: "#D97706" },
                    { label: "Failed", color: "#DC2626" },
                  ]
                : [
                    { label: "Sent", color: "#7C3AED" },
                    { label: "Delivered", color: "#059669" },
                    { label: "Failed", color: "#DC2626" },
                  ]
              ).map(({ label, color }) => (
                <Box key={label} sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: "3px", background: color }} />
                  <Typography sx={{ fontSize: "11px", color: "#64748b", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                    {label}
                  </Typography>
                </Box>
              ))}
            </Box>
            <Box sx={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: "8px", px: 1.2, py: 0.5 }}>
              <Typography sx={{ fontSize: "11px", fontWeight: 600, color: "#16a34a", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                Delivery rate: {deliveryRate}%
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* ── Charts Row ── */}
        <Grid container spacing={1.5}>
          <Grid item xs={12} md={12}>
            <Box sx={{
              background: "rgba(255,255,255,0.7)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)",
              border: "1.5px solid rgba(240,240,247,0.5)", borderRadius: "20px", p: 2.5,
              opacity: 0,
              animation: "cardIn 0.45s cubic-bezier(0.34,1.3,0.64,1) 0.44s forwards",
            }}>
              <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 2, flexWrap: "wrap", gap: 1 }}>
                <Box>
                  <Typography sx={{ fontSize: "14px", fontWeight: 700, color: "#1a1a2e", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                    Traffic Sources – {activeService}
                  </Typography>
                  <Typography sx={{ fontSize: "11px", color: "#94a3b8", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                    {activeService === "WHATSAPP"
                      ? "Last 7 days WhatsApp sent traffic"
                      : activeService === "RCS"
                      ? "Last 7 days RCS sent traffic"
                      : "Last 7 days overview"}
                  </Typography>
                </Box>
                <Box sx={{
                  display: "flex", alignItems: "center", gap: 0.6,
                  background: chartColor === "#25D366" ? "#dcfce7" : chartColor === "#059669" ? "#dcfce7" : "#f5f3ff",
                  borderRadius: "8px", px: 1.2, py: 0.5,
                  border: `1px solid ${chartColor === "#25D366" || chartColor === "#059669" ? "#bbf7d0" : "#ede9fe"}`,
                }}>
                  <Box sx={{ width: 7, height: 7, borderRadius: "50%", background: chartColor }} />
                  <Typography sx={{
                    fontSize: "10px", fontWeight: 600,
                    color: chartColor,
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                  }}>
                    {activeService}
                  </Typography>
                </Box>
              </Box>

              {/* Chart content */}
              {activeService === "WHATSAPP" && last7DaysWa.isLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                  <CircularProgress size={28} sx={{ color: "#25D366" }} />
                </Box>
              ) : activeService === "WHATSAPP" && last7DaysWa.isError ? (
                <Typography sx={{ color: "#DC2626", textAlign: "center", py: 6 }}>
                  Failed to load WhatsApp traffic.
                </Typography>
              ) : activeService === "RCS" && last7DaysRcs.isLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                  <CircularProgress size={28} sx={{ color: "#059669" }} />
                </Box>
              ) : activeService === "RCS" && last7DaysRcs.isError ? (
                <Typography sx={{ color: "#DC2626", textAlign: "center", py: 6 }}>
                  Failed to load RCS traffic.
                </Typography>
              ) : chartData.length === 0 ? (
                <Box sx={{ py: 6, textAlign: "center" }}>
                  <Typography sx={{ color: "#94a3b8", fontSize: 14 }}>
                    No traffic data available for this period.
                  </Typography>
                </Box>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={chartData} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                    <defs>
                      <linearGradient id="areaG" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={chartColor} stopOpacity={0.22} />
                        <stop offset="100%" stopColor={chartColor} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94a3b8", fontFamily: "'Plus Jakarta Sans', sans-serif" }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 10, fill: "#94a3b8", fontFamily: "'Plus Jakarta Sans', sans-serif" }} axisLine={false} tickLine={false} />
                    <Tooltip content={<CustomTooltip />} cursor={{ stroke: `${chartColor}30`, strokeWidth: 18, strokeLinecap: "round" }} />
                    <Area type="monotone" dataKey="traffic"
                      stroke={chartColor} strokeWidth={2.2} fill="url(#areaG)"
                      dot={{ r: 3, fill: chartColor, strokeWidth: 2, stroke: "#fff" }}
                      activeDot={{ r: 5, fill: chartColor, strokeWidth: 2, stroke: "#fff" }} />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default Dashboard;